import { z } from "zod";
import { vnCalendarDaySchema } from "./reports.js";

/* ===========================================================================
 * US-E8.F2-0054 Transaction History + US-E8.F3-0055 Income/Expense Details
 * (@UC0064/@UC0065) — Phase 9 Transparency/Reports (read-only).
 *
 * GET /v1/funds/:id/transactions?cursor&limit&type&from&to — cursor 20/page + filter.
 * GET /v1/transactions/:id — chi tiết 1 giao dịch (thu/chi) + reversal link.
 *
 * Redaction: Member + transparency=private → ẩn CẢ list (data rỗng + redacted=true). Reversal label
 * CẢ 2: entry reversal (isReversal) 'Bút toán đảo' + tx gốc bị đảo (isReversed) 'Đã đảo'.
 * Tiền STRING minor unit (B1, no float).
 * ========================================================================= */

const MONEY_INTEGER_STRING_PATTERN = /^-?\d+$/;

/** Phân loại hiển thị theo txType (thu/chi). */
export const TX_DIRECTION = {
  INCOME: "income",
  EXPENSE: "expense",
} as const;
export type TxDirection = (typeof TX_DIRECTION)[keyof typeof TX_DIRECTION];

/** Filter loại giao dịch (query). */
export const txTypeFilterSchema = z.enum(["all", "income", "expense"]);
export type TxTypeFilter = z.infer<typeof txTypeFilterSchema>;

/**
 * Query list — cursor opaque + limit + filter loại + khoảng thời gian `from`/`to` (YYYY-MM-DD, ngày lịch Asia/Ho_Chi_Minh, `to` inclusive
 * cuối ngày — cùng luật xuất sổ, QĐ PO 01/10/2026; trước đó là ngày UTC).
 */
export const transactionsListQuerySchema = z
  .object({
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
    type: txTypeFilterSchema.optional(),
    from: vnCalendarDaySchema.optional(),
    to: vnCalendarDaySchema.optional(),
  })
  .strict();
export type TransactionsListQuery = z.infer<typeof transactionsListQuerySchema>;

/** 1 dòng trong list history. */
export const transactionListItemSchema = z
  .object({
    id: z.string().uuid(),
    txType: z.string(),
    direction: z.enum([TX_DIRECTION.INCOME, TX_DIRECTION.EXPENSE]),
    amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    memo: z.string().nullable(),
    createdAt: z.string().datetime(),
    /** entry NÀY là bút toán đảo (reversesTxId non-null) → label 'Khoản ghi ngược (sửa sai)' (xám; QĐ PO 27/09/2026, P5.3 — chữ cũ 'Bút toán đảo'). */
    isReversal: z.boolean(),
    /** tx gốc NÀY đã bị 1 reversal đảo → label 'Đã đảo'. */
    isReversed: z.boolean(),
  })
  .strict();
export type TransactionListItem = z.infer<typeof transactionListItemSchema>;

/**
 * Response list. `data` rỗng + `redacted=true` khi Member + transparency=private (ẩn CẢ list — PO chốt).
 * Envelope `{ data, pageInfo }` khớp quy ước repo.
 */
export const transactionsListResponseSchema = z
  .object({
    data: z.array(transactionListItemSchema),
    pageInfo: z.object({
      hasNext: z.boolean(),
      nextCursor: z.string().nullable(),
    }),
    redacted: z.boolean(),
  })
  .strict();
export type TransactionsListResponse = z.infer<
  typeof transactionsListResponseSchema
>;

/* ---- 0055 detail ---- */
/** 1 line bút toán (account kind + side + amount) cho detail. */
export const transactionLineSchema = z
  .object({
    accountKind: z.string(),
    side: z.enum(["DEBIT", "CREDIT"]),
    amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
  })
  .strict();
export type TransactionLine = z.infer<typeof transactionLineSchema>;

/**
 * Detail 1 giao dịch (GET /v1/transactions/:id). Thu/chi chung shape + link reversal. amountMinor
 * no-format. `reversesTxId`/`reversedByTxId` để client link 'Xem bút toán gốc' / hiển thị 'Đã đảo'.
 */
export const transactionDetailResponseSchema = z
  .object({
    id: z.string().uuid(),
    fundId: z.string().uuid(),
    txType: z.string(),
    direction: z.enum([TX_DIRECTION.INCOME, TX_DIRECTION.EXPENSE]),
    amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    memo: z.string().nullable(),
    createdAt: z.string().datetime(),
    createdBy: z.string().uuid().nullable(),
    /** link expense (chi) nếu có. */
    expenseId: z.string().uuid().nullable(),
    /** link member_due (thu) nếu có. */
    memberDueId: z.string().uuid().nullable(),
    lines: z.array(transactionLineSchema),
    isReversal: z.boolean(),
    isReversed: z.boolean(),
    /**
     * tx gốc mà entry này đảo (khi isReversal). ⚠️ CÓ THỂ `null` dù `isReversal = true`: bản gốc không hiển thị được với người dùng (trung tính —
     * FIX-1315 M2 · @gate P5.3) ⇒ server không lộ id của nó; client ẩn nút "Xem bút toán gốc" khi `null`.
     */
    reversesTxId: z.string().uuid().nullable(),
    /** tx reversal đã đảo entry này (khi isReversed). */
    reversedByTxId: z.string().uuid().nullable(),
  })
  .strict();
export type TransactionDetailResponse = z.infer<
  typeof transactionDetailResponseSchema
>;
